import type { FC } from "react";
import { Link } from "@tanstack/react-router";
import { cx } from "@/utils/cx";

/**
 * Sdílená sekční navigace pro hub stránky (Nastavení, Testovací nástroje).
 *
 * `SectionTabs` je horní underline lišta ve stylu UUI settings-pages — třídy
 * odpovídají `underline` typu z {@link ../application/tabs/tabs.tsx}, ale lišta
 * je postavená na routovacích `<Link>`, protože react-aria `Tabs` routing neumí.
 * `SectionPills` je volitelný druhý řádek s položkami aktivní skupiny.
 *
 * Komponenty jsou čistě prezentační — nevědí nic o konkrétních sekcích ani
 * o feature gatingu; filtrování řeší volající (viz `settingsNav.ts`).
 */

export interface SectionNavItem {
  /** Absolutní cesta v SPA, např. `/settings/connectors`. */
  to: string;
  /** Přeložený popisek. */
  label: string;
  /** Volitelná ikona (pouze pills). */
  icon?: FC<{ className?: string }>;
}

interface SectionTabsProps {
  items: SectionNavItem[];
  /** Cesta aktivního tabu, nebo `null` když žádný tab neodpovídá. */
  activeTo: string | null;
  "aria-label"?: string;
}

export function SectionTabs({ items, activeTo, "aria-label": ariaLabel }: SectionTabsProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className="relative flex gap-3 overflow-x-auto before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-border-secondary"
    >
      {items.map((item) => {
        const active = item.to === activeTo;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? "page" : undefined}
            className={cx(
              "z-10 shrink-0 border-b-2 px-0.5 pb-2.5 text-sm font-semibold whitespace-nowrap transition duration-100 ease-linear",
              active
                ? "border-fg-brand-primary_alt text-brand-secondary"
                : "border-transparent text-quaternary hover:border-fg-brand-primary_alt hover:text-brand-secondary",
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

interface SectionPillsProps {
  items: SectionNavItem[];
  /** Aktuální pathname — položka je aktivní i pro své pod-cesty. */
  pathname: string;
  "aria-label"?: string;
}

export function SectionPills({ items, pathname, "aria-label": ariaLabel }: SectionPillsProps) {
  return (
    <nav aria-label={ariaLabel} className="flex flex-wrap gap-1">
      {items.map(({ to, label, icon: Icon }) => {
        const active = pathname === to || pathname.startsWith(to + "/");
        return (
          <Link
            key={to}
            to={to}
            aria-current={active ? "page" : undefined}
            className={cx(
              "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition",
              active ? "bg-brand-primary_alt text-brand-secondary" : "text-secondary hover:bg-primary_hover",
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" aria-hidden="true" />}
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

/** Placeholder lišty, dokud se nenačtou license features (jinak by se taby přeskládaly). */
export function SectionNavSkeleton() {
  return <div className="h-9 animate-pulse rounded-lg bg-secondary" aria-hidden="true" />;
}
